/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Sources } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import { extractProductNameFromARI } from '../../../../utils/ari';

import { assignedAppsCountMessages } from './messages';

const PRODUCT_NAME_MAP: Record<string, string> = {
	googledrive: 'google_drive',
	googlecalendar: 'google_calendar',
	microsoftteams: 'teams',
	microsoftonedrive: 'microsoft_onedrive',
	microsoftsharepoint: 'microsoft_sharepoint',
	microsoftpowerbi: 'microsoft_power_bi',
	atlas: 'atlas_goal',
};

const normalizeSourceApp = (appSource: string): string => {
	const lower = appSource.toLowerCase().trim();
	return PRODUCT_NAME_MAP[lower] ?? lower;
};

const styles = cssMap({
	iconsContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
		paddingLeft: token('space.025'),
	},
	iconsContainerNoPaddingLeft: {
		paddingLeft: token('space.0'),
	},
	appIcon: {
		width: '16px',
		height: '16px',
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		position: 'relative',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.inverse'),
		'&:empty': {
			display: 'none',
			width: '0px',
			height: '0px',
		},
	},
});

const ICON_ROTATIONS: Record<number, number> = {
	1: -6,
	3: 8,
};

export const AppIconWrapper = ({
	appSource,
	iconUrl,
	zIndex,
	isOverlapping,
	index,
}: {
	appSource: string;
	iconUrl?: string;
	zIndex: number;
	isOverlapping: boolean;
	index?: number;
}): JSX.Element | null => {
	if (!iconUrl) {
		return null;
	}

	const normalizedAppSource = normalizeSourceApp(appSource);
	const rotation =
		index !== undefined && fg('rovo_chat_enable_twg_trace_team26')
			? ICON_ROTATIONS[index]
			: undefined;

	return (
		<Box
			xcss={styles.appIcon}
			style={{
				zIndex,
				marginLeft: isOverlapping ? '-4px' : undefined,
				transform: rotation ? `rotate(${rotation}deg)` : undefined,
			}}
		>
			<Image src={iconUrl} alt={normalizedAppSource} width={16} height={16} />
		</Box>
	);
};

/** Row container for overlapping app icons. Reused by prompt cards and source icons. */
export const SourceIconsRow = ({
	children,
	noPaddingLeft,
}: {
	children: React.ReactNode;
	noPaddingLeft?: boolean;
}): JSX.Element => (
	<Box xcss={cx(styles.iconsContainer, noPaddingLeft && styles.iconsContainerNoPaddingLeft)}>
		{children}
	</Box>
);

export type SourceIconsProps = {
	sources: Sources;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
};

const extractAppSourcesWithCounts = (sources: Sources): Array<[string, number]> => {
	const appSourceCounts = new Map<string, number>();

	sources.forEach(({ product, ari }) => {
		let appSource: string | null = null;

		if (product) {
			appSource = normalizeSourceApp(product);
		}

		if (!appSource && ari) {
			const productName = extractProductNameFromARI(ari);
			if (productName) {
				appSource = normalizeSourceApp(productName);
			}
		}

		if (appSource) {
			appSourceCounts.set(appSource, (appSourceCounts.get(appSource) || 0) + 1);
		}
	});

	return Array.from(appSourceCounts.entries()).sort((a, b) => b[1] - a[1]);
};

export const SourceIcons = ({ sources }: SourceIconsProps): JSX.Element | null => {
	const appSourcesWithCounts = React.useMemo(() => extractAppSourcesWithCounts(sources), [sources]);

	const [storeAllSources] = useEnabledKnowledgeSources();

	const appIconUrlMap = React.useMemo(() => {
		return new Map(
			storeAllSources.map((source) => [normalizeSourceApp(source.source), source.iconUrl]),
		);
	}, [storeAllSources]);

	// Filter to only include sources with iconUrls, then take top 3 or 4 for TWG flag
	const topAppSourcesWithIcons = React.useMemo(() => {
		return appSourcesWithCounts
			.slice(0, fg('rovo_chat_enable_twg_trace_team26') ? 4 : 3)
			.map(([appSource]) => appSource);
	}, [appSourcesWithCounts]);

	if (topAppSourcesWithIcons.length === 0) {
		return null;
	}

	return (
		<SourceIconsRow noPaddingLeft={fg('rovo_chat_enable_twg_trace_team26')}>
			{topAppSourcesWithIcons.map((appSource, index) => {
				const normalizedSource = normalizeSourceApp(appSource);
				const iconUrl = appIconUrlMap.get(normalizedSource);
				const isOverlapping = index > 0;
				return (
					<AppIconWrapper
						key={`${appSource}-${normalizedSource}`}
						appSource={normalizedSource}
						iconUrl={iconUrl}
						zIndex={
							fg('rovo_chat_enable_twg_trace_team26')
								? index + 1
								: topAppSourcesWithIcons.length - index
						}
						isOverlapping={isOverlapping}
						index={fg('rovo_chat_enable_twg_trace_team26') ? index : undefined}
					/>
				);
			})}
		</SourceIconsRow>
	);
};

export type AssignedAppsIconsProps = {
	appIds: string[];
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
};

/** Renders assigned app icons for prompt cards: single app = icon + name, multiple = overlapping icons + "X apps". */
export const AssignedAppsIcons: React.NamedExoticComponent<AssignedAppsIconsProps> = React.memo(
	function AssignedAppsIcons({ appIds, assistanceServiceParams }: AssignedAppsIconsProps) {
		const { formatMessage } = useIntl();
		const [storeAllSources] = useEnabledKnowledgeSources();

		const sourceMap = React.useMemo(() => {
			const map = new Map<string, { iconUrl: string; friendlyName: string }>();
			storeAllSources.forEach((s) => {
				map.set(normalizeSourceApp(s.source), {
					iconUrl: s.iconUrl,
					friendlyName: s.friendlyName,
				});
			});
			return map;
		}, [storeAllSources]);

		const appsWithConfig = React.useMemo(() => {
			return appIds
				.map((id) => {
					const normalized = normalizeSourceApp(id);
					const config = sourceMap.get(normalized);
					return config ? { source: id, ...config } : null;
				})
				.filter((a): a is NonNullable<typeof a> => a !== null);
		}, [appIds, sourceMap]);

		if (!assistanceServiceParams || appsWithConfig.length === 0) {
			return null;
		}

		if (appsWithConfig.length === 1) {
			const app = appsWithConfig[0];
			return (
				<React.Fragment>
					<SourceIconsRow>
						<AppIconWrapper
							appSource={app.source}
							iconUrl={app.iconUrl}
							zIndex={1}
							isOverlapping={false}
						/>
					</SourceIconsRow>
					<Text color="color.text.subtlest" size="small">
						{app.friendlyName}
					</Text>
				</React.Fragment>
			);
		}

		const maxIcons = 3;
		const displayApps = appsWithConfig.slice(0, maxIcons);
		return (
			<React.Fragment>
				<SourceIconsRow>
					{displayApps.map((app, index) => (
						<AppIconWrapper
							key={app.source}
							appSource={app.source}
							iconUrl={app.iconUrl}
							zIndex={displayApps.length - index}
							isOverlapping={index > 0}
						/>
					))}
				</SourceIconsRow>
				<Text color="color.text.subtlest" size="small">
					{formatMessage(assignedAppsCountMessages.appsCount, { count: appIds.length })}
				</Text>
			</React.Fragment>
		);
	},
);
